import BaseController from "./base_controller.js";
import { api, APIError, API_BASE_URL } from "/js/services/api.js";

const EUR_FORMATTER = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const EUR0_FORMATTER = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const INT_FORMATTER = new Intl.NumberFormat("fr-FR", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "long",
  year: "numeric",
});

const DEBOUNCE_MS = 500;
const RATES = ["20.00", "10.00", "5.50", "2.10", "0.00"];

const RATE_LABELS = {
  "20.00": "20 % normal",
  "10.00": "10 % intermédiaire",
  "5.50": "5,5 % réduit",
  "2.10": "2,1 % particulier",
  "0.00": "0 % / exonéré",
};

export default class TvaDeclarationFormController extends BaseController {
  static targets = [
    "errorBanner", "errorMessage",
    "dashboard", "declarationPanel", "prepareButton",
    "historyTbody",
    // Page head
    "exerciseYear", "pageHeadSub",
    // Stats
    "regimeStatValue", "regimeStatDesc",
    "caStatLabel", "caStatValue", "caStatDesc",
    "marginStatLabel", "marginStatValue", "marginStatDesc",
    "periodicityStatValue", "periodicityStatDesc",
    // Statut TVA card
    "statusPill", "statusPillLabel",
    "statusLadderFranchise", "statusLadderSubject",
    "subjectRegimeName", "subjectRegimeDesc", "statusFootnote",
    // Prochaine échéance / déclaration card
    "nextDeadlineFranchise", "nextDeadlineSubject",
    "franchiseHeadline", "franchiseExplain",
    "franchiseMargin", "franchiseMajoredThreshold",
    "franchisePlannedRow", "franchisePlannedDate",
    "subjectDeadlineTitle", "subjectDeadlineDesc",
    // Jauges
    "gaugesTitle", "franchiseGauges", "subjectGauge",
    "gaugeBasicAmount", "gaugeBasicFill", "gaugeBasicFoot",
    "gaugeMajoredAmount", "gaugeMajoredFill",
    "gaugeMicroAmount", "gaugeMicroFill", "gaugeMicroFoot",
    // Declaration panel
    "declarationOverline", "declarationTitle", "declarationSubtitle",
    "collectedTbody", "collectedTotal",
    "deductibleInput", "deductibleTotal",
    "acomptesSection",
    "netAmount", "netFormula",
    "breakdownCollected", "breakdownDeductible", "breakdownNet",
    "downloadLink", "pdfDownloadLink",
  ];

  async connect() {
    this._declarationId = null;
    this._debounceHandle = null;
    this._lastSavedSnapshot = null;
    this._collectedCents = 0;
    this.element.addEventListener(
      "tva:declaration-submitted",
      this._onDeclarationSubmitted.bind(this),
    );

    let tva;
    try {
      const payload = await api.get("/tva");
      tva = payload?.data ?? {};
    } catch (error) {
      this._showError(
        error instanceof APIError
          ? error.message
          : "Impossible de charger l'état de la TVA.",
      );
      return;
    }

    const [thresholds, status, alerts, calendar] = await Promise.allSettled([
      api.get("/me/vat-thresholds"),
      api.get("/me/vat-status"),
      api.get("/me/vat-franchise-alerts"),
      api.get("/me/vat-calendar-events"),
    ]);

    this._renderDashboard(tva, {
      thresholds: this._payload(thresholds),
      status: this._payload(status),
      alerts: this._listPayload(alerts),
      calendar: this._listPayload(calendar),
    });

    this._applyInitialState(tva);
  }

  showDeclaration(event) {
    if (event) event.preventDefault();
    if (!this._declarationId) {
      this._showError(
        "Aucune déclaration en cours — la prochaine déclaration sera proposée à la fin de la période.",
      );
      return;
    }
    this._togglePanels(true);
  }

  deductibleInputTargetConnected(input) {
    input.addEventListener("input", this._onDeductibleInput.bind(this));
  }

  // ---------------------------------------------------------------------------
  // Dashboard rendering (régime / seuils / échéance)
  // ---------------------------------------------------------------------------

  _renderDashboard(tva, extra) {
    const subjectionStatus = tva.subjectionStatus ?? "franchise";
    const regimeChosen = tva.regimeChosen ?? null;
    const ca3Periodicity = tva.ca3Periodicity ?? null;
    const isFranchise = subjectionStatus === "franchise";

    const year = this._resolveYear(tva.currentDraft ?? null);
    if (this.hasExerciseYearTarget) this.exerciseYearTarget.textContent = String(year);

    const binding = this._pickBindingCategory(extra.thresholds?.categories ?? []);
    const caYtdTotal = this._caYtdTotal(extra.thresholds);

    this._renderRegimeStat(isFranchise, regimeChosen, subjectionStatus);
    this._renderPeriodicityStat(isFranchise, regimeChosen, ca3Periodicity);
    this._renderPageHeadSub(isFranchise, regimeChosen, binding);
    this._renderStatusCard(isFranchise, regimeChosen, ca3Periodicity);

    if (isFranchise) {
      this._renderFranchiseState(binding, caYtdTotal, year, extra);
    } else {
      this._renderSubjectState(binding, caYtdTotal, year, regimeChosen, extra.thresholds);
    }
  }

  _renderRegimeStat(isFranchise, regimeChosen, subjectionStatus) {
    if (this.hasRegimeStatValueTarget) {
      this.regimeStatValueTarget.textContent = this._regimeShortLabel(
        isFranchise,
        regimeChosen,
      );
    }
    if (this.hasRegimeStatDescTarget) {
      this.regimeStatDescTarget.textContent = isFranchise
        ? "Aucune TVA facturée"
        : "TVA facturée sur vos ventes";
    }
  }

  _renderPeriodicityStat(isFranchise, regimeChosen, ca3Periodicity) {
    let value = "—";
    let desc = "";
    if (isFranchise) {
      value = "CA12";
      desc = "Régime simplifié par défaut à la bascule";
    } else if (regimeChosen === "simplified_ca12") {
      value = "CA12";
      desc = "Réel simplifié, annuelle";
    } else if (regimeChosen === "normal_ca3") {
      value = "CA3";
      desc = ca3Periodicity === "monthly"
        ? "Réel normal, mensuelle"
        : ca3Periodicity === "quarterly"
          ? "Réel normal, trimestrielle"
          : "Réel normal";
    }
    if (this.hasPeriodicityStatValueTarget) this.periodicityStatValueTarget.textContent = value;
    if (this.hasPeriodicityStatDescTarget) this.periodicityStatDescTarget.textContent = desc;
  }

  _renderPageHeadSub(isFranchise, regimeChosen, binding) {
    if (!this.hasPageHeadSubTarget) return;
    const regime = this._regimeFullLabel(isFranchise, regimeChosen);
    if (isFranchise && binding) {
      this.pageHeadSubTarget.textContent =
        `Régime ${regime} · seuil de bascule à ${EUR0_FORMATTER.format(binding.thresholdBasic)}`;
    } else {
      this.pageHeadSubTarget.textContent = `Régime ${regime}`;
    }
  }

  _renderStatusCard(isFranchise, regimeChosen, ca3Periodicity) {
    if (this.hasStatusPillLabelTarget) {
      this.statusPillLabelTarget.textContent = isFranchise
        ? "franchise en base"
        : this._regimeFullLabel(isFranchise, regimeChosen).toLowerCase();
    }
    if (this.hasStatusPillTarget) {
      this.statusPillTarget.className = isFranchise
        ? "nm-pill nm-pill--blue"
        : "nm-pill nm-pill--olive";
    }
    if (this.hasStatusLadderFranchiseTarget) this.statusLadderFranchiseTarget.hidden = !isFranchise;
    if (this.hasStatusLadderSubjectTarget) this.statusLadderSubjectTarget.hidden = isFranchise;

    if (!isFranchise) {
      if (this.hasSubjectRegimeNameTarget) {
        this.subjectRegimeNameTarget.textContent = this._regimeShortLabel(false, regimeChosen);
      }
      if (this.hasSubjectRegimeDescTarget) {
        const periodicity = regimeChosen === "normal_ca3"
          ? (ca3Periodicity === "monthly" ? "mensuelle (CA3)" : "trimestrielle (CA3)")
          : "annuelle (CA12)";
        this.subjectRegimeDescTarget.textContent =
          `Vos factures affichent la TVA · déclaration ${periodicity}`;
      }
      if (this.hasStatusFootnoteTarget) {
        this.statusFootnoteTarget.textContent =
          "Vous êtes assujetti à la TVA : Notamicro calcule votre TVA collectée à partir de vos factures émises et la TVA déductible à partir de vos factures reçues.";
      }
    }
  }

  _renderFranchiseState(binding, caYtdTotal, year, extra) {
    if (this.hasNextDeadlineFranchiseTarget) this.nextDeadlineFranchiseTarget.hidden = false;
    if (this.hasNextDeadlineSubjectTarget) this.nextDeadlineSubjectTarget.hidden = true;
    if (this.hasFranchiseGaugesTarget) this.franchiseGaugesTarget.hidden = false;
    if (this.hasSubjectGaugeTarget) this.subjectGaugeTarget.hidden = true;

    const usedBasicPct = this._pct(binding?.caYtd ?? 0, binding?.thresholdBasic ?? 0);
    const usedMajoredPct = this._pct(binding?.caYtd ?? 0, binding?.thresholdMajored ?? 0);

    // Stats
    if (this.hasCaStatLabelTarget) this.caStatLabelTarget.textContent = `CA cumulé ${year}`;
    if (this.hasCaStatValueTarget) this.caStatValueTarget.textContent = INT_FORMATTER.format(caYtdTotal);
    if (this.hasCaStatDescTarget) {
      this.caStatDescTarget.textContent = `${usedBasicPct} % du seuil franchise`;
      this.caStatDescTarget.classList.toggle("is-warning", usedBasicPct >= 90);
    }
    if (this.hasMarginStatLabelTarget) this.marginStatLabelTarget.textContent = "Marge avant bascule";
    if (this.hasMarginStatValueTarget) {
      this.marginStatValueTarget.textContent = INT_FORMATTER.format(binding?.marginBasicEur ?? 0);
    }
    if (this.hasMarginStatDescTarget) this.marginStatDescTarget.textContent = "Avant le seuil de franchise";

    // Card prochaine échéance
    const level = this._alertLevel(extra.alerts);
    const plannedDate = this._plannedSwitchDate(extra.calendar);
    let headline = "Aucune échéance immédiate";
    let explain = `Vous êtes en franchise en base : aucune déclaration de TVA à produire tant que le seuil de ${EUR0_FORMATTER.format(binding?.thresholdBasic ?? 0)} n'est pas franchi.`;
    if (level === "critical") {
      headline = "Seuil majoré franchi";
      explain = "Le seuil majoré est dépassé : vous devenez redevable de la TVA. Préparez votre passage au régime réel.";
    } else if (plannedDate) {
      headline = "Bascule planifiée";
      explain = "Vous avez dépassé le seuil de franchise sur l'année : passage au régime réel au 1er janvier prochain.";
    } else if (level === "warning") {
      headline = "Seuil bientôt atteint";
      explain = "Vous approchez du seuil de franchise. Surveillez votre chiffre d'affaires pour anticiper la bascule.";
    }
    if (this.hasFranchiseHeadlineTarget) this.franchiseHeadlineTarget.textContent = headline;
    if (this.hasFranchiseExplainTarget) this.franchiseExplainTarget.textContent = explain;
    if (this.hasFranchiseMarginTarget) this.franchiseMarginTarget.textContent = EUR0_FORMATTER.format(binding?.marginBasicEur ?? 0);
    if (this.hasFranchiseMajoredThresholdTarget) this.franchiseMajoredThresholdTarget.textContent = EUR0_FORMATTER.format(binding?.thresholdMajored ?? 0);
    if (this.hasFranchisePlannedRowTarget) this.franchisePlannedRowTarget.hidden = !plannedDate;
    if (plannedDate && this.hasFranchisePlannedDateTarget) {
      this.franchisePlannedDateTarget.textContent = DATE_FORMATTER.format(plannedDate);
    }

    // Jauges
    if (this.hasGaugesTitleTarget) this.gaugesTitleTarget.textContent = "Marges avant bascule";
    if (this.hasGaugeBasicAmountTarget) {
      this.gaugeBasicAmountTarget.textContent =
        `${EUR0_FORMATTER.format(binding?.caYtd ?? 0)} / ${EUR0_FORMATTER.format(binding?.thresholdBasic ?? 0)}`;
    }
    if (this.hasGaugeBasicFillTarget) {
      this.gaugeBasicFillTarget.style.width = `${usedBasicPct}%`;
      this.gaugeBasicFillTarget.classList.toggle("is-warning", usedBasicPct >= 90);
    }
    if (this.hasGaugeBasicFootTarget) this.gaugeBasicFootTarget.textContent = `${usedBasicPct} % atteint · alerte à 90 %`;
    if (this.hasGaugeMajoredAmountTarget) {
      this.gaugeMajoredAmountTarget.textContent =
        `${EUR0_FORMATTER.format(binding?.caYtd ?? 0)} / ${EUR0_FORMATTER.format(binding?.thresholdMajored ?? 0)}`;
    }
    if (this.hasGaugeMajoredFillTarget) this.gaugeMajoredFillTarget.style.width = `${usedMajoredPct}%`;
  }

  _renderSubjectState(binding, caYtdTotal, year, regimeChosen, thresholds) {
    if (this.hasNextDeadlineFranchiseTarget) this.nextDeadlineFranchiseTarget.hidden = true;
    if (this.hasNextDeadlineSubjectTarget) this.nextDeadlineSubjectTarget.hidden = false;
    if (this.hasFranchiseGaugesTarget) this.franchiseGaugesTarget.hidden = true;
    if (this.hasSubjectGaugeTarget) this.subjectGaugeTarget.hidden = false;

    const microThreshold = thresholds?.mixedTotal?.thresholdMicroExit ?? binding?.thresholdMicroExit ?? 0;
    const microCa = thresholds?.mixedTotal?.caYtd ?? binding?.caYtd ?? caYtdTotal;
    const microMargin = thresholds?.mixedTotal?.marginEur ?? binding?.marginMicroEur ?? 0;
    const usedMicroPct = this._pct(microCa, microThreshold);

    // Stats
    if (this.hasCaStatLabelTarget) this.caStatLabelTarget.textContent = `CA cumulé ${year}`;
    if (this.hasCaStatValueTarget) this.caStatValueTarget.textContent = INT_FORMATTER.format(caYtdTotal);
    if (this.hasCaStatDescTarget) {
      this.caStatDescTarget.textContent = `${usedMicroPct} % du seuil micro`;
      this.caStatDescTarget.classList.toggle("is-warning", usedMicroPct >= 90);
    }
    if (this.hasMarginStatLabelTarget) this.marginStatLabelTarget.textContent = "Marge avant sortie micro";
    if (this.hasMarginStatValueTarget) this.marginStatValueTarget.textContent = INT_FORMATTER.format(microMargin);
    if (this.hasMarginStatDescTarget) this.marginStatDescTarget.textContent = "Avant la sortie du régime micro";

    // Card prochaine déclaration : alimentée par le draft dans _renderDraft / fallback
    this._renderSubjectDeadlineFromDraft(null);

    // Jauge sortie micro
    if (this.hasGaugesTitleTarget) this.gaugesTitleTarget.textContent = "Marge avant sortie micro";
    if (this.hasGaugeMicroAmountTarget) {
      this.gaugeMicroAmountTarget.textContent =
        `${EUR0_FORMATTER.format(microCa)} / ${EUR0_FORMATTER.format(microThreshold)}`;
    }
    if (this.hasGaugeMicroFillTarget) {
      this.gaugeMicroFillTarget.style.width = `${usedMicroPct}%`;
      this.gaugeMicroFillTarget.classList.toggle("is-warning", usedMicroPct >= 90);
    }
    if (this.hasGaugeMicroFootTarget) {
      this.gaugeMicroFootTarget.textContent = `${usedMicroPct} % atteint · sortie du régime micro au-delà`;
    }
  }

  _renderSubjectDeadlineFromDraft(draft) {
    if (!this.hasNextDeadlineSubjectTarget) return;
    if (draft) {
      if (this.hasSubjectDeadlineTitleTarget) {
        this.subjectDeadlineTitleTarget.textContent =
          `Déclaration ${this._regimeLabel(draft.regimeType)}`;
      }
      if (this.hasSubjectDeadlineDescTarget) {
        const start = draft.periodStartDate ? new Date(draft.periodStartDate) : null;
        const end = draft.periodEndDate ? new Date(draft.periodEndDate) : null;
        this.subjectDeadlineDescTarget.textContent = (start && end)
          ? `Période du ${DATE_FORMATTER.format(start)} au ${DATE_FORMATTER.format(end)} · à préparer puis télédéclarer sur impots.gouv.fr.`
          : "Déclaration en cours · à télédéclarer sur impots.gouv.fr.";
      }
    } else {
      if (this.hasSubjectDeadlineTitleTarget) {
        this.subjectDeadlineTitleTarget.textContent = "Prochaine déclaration";
      }
      if (this.hasSubjectDeadlineDescTarget) {
        this.subjectDeadlineDescTarget.textContent =
          "Votre prochaine déclaration sera disponible à la fin de la période.";
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Declaration panel (historique + draft)
  // ---------------------------------------------------------------------------

  _applyInitialState(data) {
    const declarations = Array.isArray(data.declarations) ? data.declarations : [];
    const currentDraft = data.currentDraft ?? null;

    this._renderHistory(declarations);

    if (currentDraft) {
      this._declarationId = currentDraft.id;
      this._renderDraft(currentDraft);
      this._renderSubjectDeadlineFromDraft(currentDraft);
      this._togglePanels(true);
    } else {
      this._declarationId = null;
      this._togglePanels(false);
    }
  }

  _renderHistory(declarations) {
    if (!this.hasHistoryTbodyTarget) return;
    const tbody = this.historyTbodyTarget;
    tbody.replaceChildren();
    if (declarations.length === 0) {
      const row = document.createElement("tr");
      const cell = document.createElement("td");
      cell.colSpan = 5;
      cell.className = "is-muted";
      cell.textContent = "Aucune déclaration enregistrée pour le moment.";
      row.appendChild(cell);
      tbody.appendChild(row);
      return;
    }
    for (const declaration of declarations) {
      tbody.appendChild(this._buildHistoryRow(declaration));
    }
  }

  _buildHistoryRow(declaration) {
    const row = document.createElement("tr");

    const periodCell = document.createElement("td");
    periodCell.textContent = this._formatPeriodShort(
      declaration.periodStartDate,
      declaration.periodEndDate,
    );
    row.appendChild(periodCell);

    const typeCell = document.createElement("td");
    typeCell.textContent = this._regimeLabel(declaration.regimeType);
    row.appendChild(typeCell);

    const submittedCell = document.createElement("td");
    submittedCell.className = "is-muted";
    submittedCell.textContent = declaration.submittedAt
      ? this._formatDate(declaration.submittedAt)
      : "—";
    row.appendChild(submittedCell);

    const amountCell = document.createElement("td");
    amountCell.className = "is-amount";
    amountCell.textContent = this._formatNetAmount(declaration);
    row.appendChild(amountCell);

    const statusCell = document.createElement("td");
    statusCell.appendChild(this._buildStatusPill(declaration.submissionStatus));
    row.appendChild(statusCell);

    return row;
  }

  _buildStatusPill(status) {
    const pill = document.createElement("span");
    pill.className = status === "submitted"
      ? "nm-pill nm-pill--olive"
      : "nm-pill nm-pill--blue";
    const dot = document.createElement("span");
    dot.className = "nm-pill__dot";
    pill.appendChild(dot);
    pill.appendChild(
      document.createTextNode(status === "submitted" ? "déposée" : "brouillon"),
    );
    return pill;
  }

  _renderDraft(draft) {
    if (this.hasDeclarationOverlineTarget) {
      this.declarationOverlineTarget.textContent =
        `Déclaration de TVA — ${this._regimeLongLabel(draft.regimeType)}`;
    }
    if (this.hasDeclarationTitleTarget) {
      this.declarationTitleTarget.textContent = this._buildDraftTitle(draft);
    }
    if (this.hasDeclarationSubtitleTarget) {
      const start = draft.periodStartDate ? new Date(draft.periodStartDate) : null;
      const end = draft.periodEndDate ? new Date(draft.periodEndDate) : null;
      this.declarationSubtitleTarget.textContent = (start && end)
        ? `Période du ${DATE_FORMATTER.format(start)} au ${DATE_FORMATTER.format(end)} · à télédéclarer sur impots.gouv.fr`
        : "À télédéclarer sur impots.gouv.fr";
    }

    this._collectedCents = this._sumByRate(draft.caCollectedByRate ?? {});
    this._renderCollectedTable(draft.caCollectedByRate ?? {});
    this._renderDeductibleInputs(draft.caDeductibleByRate ?? {});
    this._toggleAcomptes(draft.regimeType);
    this._renderDownloadLinks(draft.id);
    this._recomputeTotals();
    this._lastSavedSnapshot = this._snapshotFromDeductible(
      draft.caDeductibleByRate ?? {},
    );
  }

  _renderCollectedTable(collectedByRate) {
    if (!this.hasCollectedTbodyTarget) return;
    const tbody = this.collectedTbodyTarget;
    tbody.replaceChildren();
    for (const rate of RATES) {
      const cents = Number(collectedByRate[rate] ?? 0);
      const row = document.createElement("tr");
      const rateCell = document.createElement("td");
      rateCell.textContent = RATE_LABELS[rate] ?? `${rate} %`;
      row.appendChild(rateCell);
      const amountCell = document.createElement("td");
      amountCell.className = "is-amount";
      amountCell.textContent = EUR_FORMATTER.format(cents / 100);
      row.appendChild(amountCell);
      tbody.appendChild(row);
    }
  }

  _renderDeductibleInputs(deductibleByRate) {
    if (!this.hasDeductibleInputTarget) return;
    for (const input of this.deductibleInputTargets) {
      const rate = input.dataset.rate;
      const cents = Number(deductibleByRate[rate] ?? 0);
      input.value = (cents / 100).toFixed(2);
    }
  }

  _toggleAcomptes(regimeType) {
    if (!this.hasAcomptesSectionTarget) return;
    const isCa12 = typeof regimeType === "string" && regimeType.includes("ca12");
    this.acomptesSectionTarget.hidden = !isCa12;
  }

  _recomputeTotals() {
    const deductibleCents = this._collectDeductibleCents();
    const netCents = this._collectedCents - deductibleCents;

    if (this.hasCollectedTotalTarget) {
      this.collectedTotalTarget.textContent = EUR_FORMATTER.format(this._collectedCents / 100);
    }
    if (this.hasDeductibleTotalTarget) {
      this.deductibleTotalTarget.textContent = EUR_FORMATTER.format(deductibleCents / 100);
    }
    if (this.hasNetAmountTarget) {
      this.netAmountTarget.textContent = EUR_FORMATTER.format(netCents / 100);
    }
    if (this.hasNetFormulaTarget) {
      this.netFormulaTarget.textContent =
        `${EUR0_FORMATTER.format(this._collectedCents / 100)} collecté − ${EUR0_FORMATTER.format(deductibleCents / 100)} déductible`;
    }
    if (this.hasBreakdownCollectedTarget) {
      this.breakdownCollectedTarget.textContent = EUR_FORMATTER.format(this._collectedCents / 100);
    }
    if (this.hasBreakdownDeductibleTarget) {
      this.breakdownDeductibleTarget.textContent = `− ${EUR_FORMATTER.format(deductibleCents / 100)}`;
    }
    if (this.hasBreakdownNetTarget) {
      this.breakdownNetTarget.textContent = EUR_FORMATTER.format(netCents / 100);
    }
  }

  _collectDeductibleCents() {
    if (!this.hasDeductibleInputTarget) return 0;
    let total = 0;
    for (const input of this.deductibleInputTargets) {
      const value = Number(input.value);
      if (Number.isFinite(value) && value > 0) total += Math.round(value * 100);
    }
    return total;
  }

  _renderDownloadLinks(declarationId) {
    const href = `${API_BASE_URL}/tva/declarations/${encodeURIComponent(declarationId)}/pdf`;
    if (this.hasDownloadLinkTarget) {
      this.downloadLinkTarget.setAttribute("href", href);
    }
    if (this.hasPdfDownloadLinkTarget) {
      this.pdfDownloadLinkTarget.setAttribute("href", href);
    }
  }

  _togglePanels(showDeclaration) {
    if (this.hasDashboardTarget) {
      this.dashboardTarget.hidden = showDeclaration;
    }
    if (this.hasDeclarationPanelTarget) {
      this.declarationPanelTarget.hidden = !showDeclaration;
    }
  }

  _onDeductibleInput() {
    this._recomputeTotals();
    if (!this._declarationId) return;
    if (this._debounceHandle !== null) {
      window.clearTimeout(this._debounceHandle);
    }
    this._debounceHandle = window.setTimeout(
      () => this._persistDeductible(),
      DEBOUNCE_MS,
    );
  }

  async _persistDeductible() {
    if (!this._declarationId) return;
    const payload = this._collectDeductiblePayload();
    if (payload === null) return;
    if (this._snapshotsEqual(payload, this._lastSavedSnapshot)) return;

    try {
      const response = await api.post(
        `/tva/declarations/${encodeURIComponent(this._declarationId)}/deductible-override`,
        { deductibleByRate: payload },
      );
      const updated = response?.data ?? null;
      if (updated) {
        this._collectedCents = this._sumByRate(updated.caCollectedByRate ?? {});
        this._renderDeductibleInputs(updated.caDeductibleByRate ?? {});
        this._recomputeTotals();
        this._lastSavedSnapshot = this._snapshotFromDeductible(
          updated.caDeductibleByRate ?? {},
        );
      } else {
        this._lastSavedSnapshot = payload;
      }
      this._hideError();
    } catch (error) {
      this._showError(
        error instanceof APIError
          ? error.message
          : "Échec de la sauvegarde de la TVA déductible.",
      );
    }
  }

  _collectDeductiblePayload() {
    if (!this.hasDeductibleInputTarget) return null;
    const payload = {};
    for (const rate of RATES) payload[rate] = 0;
    for (const input of this.deductibleInputTargets) {
      const rate = input.dataset.rate;
      if (!rate) continue;
      const value = Number(input.value);
      if (!Number.isFinite(value) || value < 0) {
        this._showError("Les montants de TVA déductible doivent être positifs.");
        return null;
      }
      payload[rate] = Math.round(value * 100);
    }
    return payload;
  }

  _onDeclarationSubmitted(event) {
    const updated = event?.detail?.declaration ?? null;
    if (!updated) return;
    this._declarationId = null;
    if (this.hasHistoryTbodyTarget) {
      const existingRow = this.historyTbodyTarget.querySelector(
        `tr[data-declaration-id="${updated.id}"]`,
      );
      if (!existingRow) {
        this.historyTbodyTarget.insertBefore(
          this._buildHistoryRow(updated),
          this.historyTbodyTarget.firstChild,
        );
      }
    }
    this._togglePanels(false);
  }

  // ---------------------------------------------------------------------------
  // Internal helpers — payload extraction & formatting
  // ---------------------------------------------------------------------------

  _payload(settled) {
    if (settled.status !== "fulfilled") return null;
    return settled.value?.data ?? null;
  }

  _listPayload(settled) {
    if (settled.status !== "fulfilled") return [];
    const data = settled.value?.data?.data;
    return Array.isArray(data) ? data : [];
  }

  _pickBindingCategory(categories) {
    if (!Array.isArray(categories) || categories.length === 0) return null;
    return categories.reduce(
      (best, current) =>
        Number(current.marginBasicEur ?? Infinity) < Number(best.marginBasicEur ?? Infinity)
          ? current
          : best,
      categories[0],
    );
  }

  _caYtdTotal(thresholds) {
    if (!thresholds) return 0;
    if (thresholds.mixedTotal && Number.isFinite(Number(thresholds.mixedTotal.caYtd))) {
      return Number(thresholds.mixedTotal.caYtd);
    }
    const categories = Array.isArray(thresholds.categories) ? thresholds.categories : [];
    return categories.reduce((sum, c) => sum + (Number(c.caYtd) || 0), 0);
  }

  _pct(value, threshold) {
    if (!threshold || threshold <= 0) return 0;
    return Math.min(100, Math.round((value / threshold) * 100));
  }

  _alertLevel(alerts) {
    if (!Array.isArray(alerts) || alerts.length === 0) return null;
    if (alerts.some((a) => a.level === "critical")) return "critical";
    if (alerts.some((a) => a.level === "warning")) return "warning";
    return null;
  }

  _plannedSwitchDate(calendar) {
    if (!Array.isArray(calendar)) return null;
    const event = calendar.find((e) => e.event === "vat_switch_planned" && e.date);
    if (!event) return null;
    const parsed = new Date(event.date);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  _resolveYear(currentDraft) {
    if (currentDraft?.periodEndDate) {
      const year = currentDraft.periodEndDate.slice(0, 4);
      if (/^\d{4}$/.test(year)) return Number(year);
    }
    return new Date().getFullYear();
  }

  _regimeShortLabel(isFranchise, regimeChosen) {
    if (isFranchise) return "Franchise";
    switch (regimeChosen) {
      case "simplified_ca12":
        return "Réel simplifié";
      case "normal_ca3":
        return "Réel normal";
      default:
        return "Réel";
    }
  }

  _regimeFullLabel(isFranchise, regimeChosen) {
    if (isFranchise) return "franchise en base";
    switch (regimeChosen) {
      case "simplified_ca12":
        return "réel simplifié (CA12)";
      case "normal_ca3":
        return "réel normal (CA3)";
      default:
        return "réel";
    }
  }

  _snapshotFromDeductible(deductibleByRate) {
    const snapshot = {};
    for (const rate of RATES) snapshot[rate] = Number(deductibleByRate[rate] ?? 0);
    return snapshot;
  }

  _snapshotsEqual(a, b) {
    if (a === null || b === null) return false;
    for (const rate of RATES) {
      if (Number(a[rate] ?? 0) !== Number(b[rate] ?? 0)) return false;
    }
    return true;
  }

  _sumByRate(map) {
    let total = 0;
    for (const value of Object.values(map ?? {})) total += Number(value) || 0;
    return total;
  }

  _formatPeriodShort(start, end) {
    const startYear = start?.slice(0, 4) ?? "";
    const endYear = end?.slice(0, 4) ?? "";
    return startYear === endYear ? startYear : `${startYear} → ${endYear}`;
  }

  _regimeLabel(regimeType) {
    switch (regimeType) {
      case "normal_ca3":
        return "CA3";
      case "simplified_ca12":
        return "CA12";
      default:
        return regimeType ?? "—";
    }
  }

  _regimeLongLabel(regimeType) {
    switch (regimeType) {
      case "normal_ca3":
        return "réel normal (CA3)";
      case "simplified_ca12":
        return "réel simplifié (CA12)";
      default:
        return "TVA";
    }
  }

  _buildDraftTitle(draft) {
    const start = draft.periodStartDate ? new Date(draft.periodStartDate) : null;
    const end = draft.periodEndDate ? new Date(draft.periodEndDate) : null;
    if (!start || !end) return "Déclaration TVA en cours";
    const regime = this._regimeLabel(draft.regimeType);
    return `${regime} — du ${DATE_FORMATTER.format(start)} au ${DATE_FORMATTER.format(end)}`;
  }

  _formatNetAmount(declaration) {
    const net = this._sumByRate(declaration.caCollectedByRate ?? {})
      - this._sumByRate(declaration.caDeductibleByRate ?? {});
    return EUR_FORMATTER.format(net / 100);
  }

  _formatDate(isoString) {
    if (!isoString) return "—";
    const parsed = new Date(isoString);
    if (Number.isNaN(parsed.getTime())) return "—";
    return DATE_FORMATTER.format(parsed);
  }

  _showError(message) {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = false;
    if (this.hasErrorMessageTarget) this.errorMessageTarget.textContent = message;
  }

  _hideError() {
    if (this.hasErrorBannerTarget) this.errorBannerTarget.hidden = true;
  }
}
